import React from 'react';
import { Award } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@contexts/AuthContext';
import { assessmentService } from '@services';
import Button from '@components/common/Button';
import ConfirmDialog from '@components/common/ConfirmDialog';
import type { Assessment } from '@app-types';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';

interface PublishAssessmentActionProps {
  assessment: Assessment;
  /** Items still "Not Assessed"; publishing needs every item evaluated. */
  notAssessedCount: number;
}

const PUBLISHABLE_STATUS = 'Pending';

/**
 * The "Publish Assessment" header action of the Program Lead's assessment page (ADO-1176). The page shows
 * no process list, so it publishes the assessment through the same endpoint as the Assessor's Publish.
 */
const PublishAssessmentAction: React.FC<PublishAssessmentActionProps> = ({ assessment, notAssessedCount }) => {
  const { isProgramLead } = usePermissions();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = React.useState(false);

  const publishMutation = useMutation({
    mutationFn: () => assessmentService.completeAssessment(assessment.assessment_id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', String(assessment.assessment_id)] }),
        queryClient.invalidateQueries({ queryKey: ['assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-table'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['supplier-assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['contract-assessments'] })
      ]);
      showToast.success('Assessment published successfully');
    },
    onError: (error) => showToast.error(getApiErrorMessage(error, 'Failed to publish assessment'))
  });

  if (!isProgramLead || assessment.assessment_status !== PUBLISHABLE_STATUS) return null;

  const handleConfirm = () => {
    setShowConfirm(false);
    publishMutation.mutate();
  };

  const hasItemsToEvaluate = notAssessedCount > 0;

  return (
    <>
      <Button
        variant="primary"
        onClick={() => setShowConfirm(true)}
        disabled={publishMutation.isPending || hasItemsToEvaluate}
        title={hasItemsToEvaluate ? `${notAssessedCount} items remaining to evaluate` : undefined}
        dataTestId="publish-assessment-button"
      >
        <Award size={16} />
        {publishMutation.isPending ? 'Publishing...' : 'Publish Assessment'}
      </Button>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Publish Assessment?"
        message="This will generate the initial assessment report and notify Contract Manager(s) and Owner(s). Continue?"
        confirmText="Publish Assessment"
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        dataTestId="publish-assessment-dialog"
      />
    </>
  );
};

export default PublishAssessmentAction;
